/* A room with everything in it and nothing at stake.
 *
 * This drives the REAL `Game`. Nothing here reimplements a mechanic: the
 * charms are the charms, the creatures are the creatures, the fire spreads
 * because `Flame` spreads it. What the playground adds is a flat room, every
 * charm in your pocket, and a panel that can put any of it in front of you —
 * so "what does the Emberwake actually do to a hedge" is a question you answer
 * by watching, in about four seconds, instead of by finding one in a run.
 *
 * It is also why this file is short. A sandbox that reimplements the game in
 * order to demonstrate it is a second game that will disagree with the first.
 */
import { Game, VW, VH, HUD_H } from '../src/game.ts';
import { Display } from '../src/display.ts';
import { Pad } from '../src/pad.ts';
import { actionLabel, swingLabel } from '../src/labels.ts';

/** The four flags a KEYBOARD speaks in — the stick speaks in a vector. */
const KEYS = ['up', 'down', 'left', 'right'];
import { T, TS, UI } from '../src/art.ts';
import { paintArena, START, isArena } from './arena.ts';
import { COLS, ROWS } from '../src/world.ts';
import { RNG } from '../src/rng.ts';
import { ENEMY_DEFS } from '../src/entities.ts';
import { ITEMS, addItem, bagOf, selectSlot, wear, HOTBAR as HOTBAR_SLOTS } from '../src/sim/inventory.ts';

const screenCanvas = document.getElementById('screen') as any;
const buffer = document.createElement('canvas');
const game = new Game(buffer);
const display = new Display(screenCanvas, buffer, 'crisp');
// A SANDBOX MUST NOT TOUCH SAVES. This shares the whole `Game`, and that
// includes the autosave and the concurrent-write guard — so opening the
// playground beside a real run claimed the slot and the HUD reported OTHER
// TAB OWNS THIS over a room that is not a run at all. Nothing here is worth
// keeping and nothing here may cost a player anything: the write path is
// closed before the first frame.
game.save = () => {};
game.clearSave = () => {};
game.saveNow = () => {};
const display2 = null; void display2;
game.onWater = (mask, topV) => display.setWater(mask, topV);
game.reset('playground');
game.startGame();
game.mode = 'play';

// --------------------------------------------------------------- the room
/** A deliberately plain room with one of everything worth trying along the
 *  north wall. Rebuilt on demand, because the whole point is that you can put
 *  it back after you have burnt it down. */
let quiet = false;
function buildRoom(hush = false) {
  /* THE ARENA IS A WILD SCREEN, because that is all there is now (2026-09-06).
   *
   * This used to hunt `game.hollows[0]` for a room to point the area at, so that FLOOR and WALL would be painted
   * with the underground palette instead of a biome's. Hollows went with the pivot: `game.hollows` has been
   * undefined ever since, so the lookup found nothing, the `if` never ran, and the line below did all the work
   * anyway. Dead since the pivot and reported by `tsc` the whole time. */
  game.loadArea();
  paintArena(game.tiles, COLS, ROWS);
  game.enemies = []; game.pods = []; game.shots = []; game.fx = [];
  game.player.x = START[0]; game.player.y = START[1]; game.player.recentre();
  if (!hush) say('room rebuilt');
  quiet = true;   // the first one is announced; the ones that heal a re-bake are not
}

// ------------------------------------------------------------------ panel
const $ = (id) => document.getElementById(id) as any;
const logEl = $('log');
function say(msg) {
  const line = document.createElement('div');
  line.innerHTML = '<b>&rsaquo;</b> ' + msg;
  logEl.prepend(line);
  while (logEl.children.length > 24) logEl.lastChild.remove();
}
/** `cls` is optional — most buttons take the default styling. */
function button(into, label, fn, cls?) {
  const b = document.createElement('button');
  b.textContent = label;
  if (cls) b.className = cls;
  b.addEventListener('click', () => { fn(b); refresh(); });
  into.appendChild(b);
  return b;
}
function slider(into, label, min, max, get, set) {
  const l = document.createElement('label');
  l.className = 'num';
  const name = document.createElement('span'); name.textContent = label;
  const input = document.createElement('input');
  input.type = 'range'; input.min = min; input.max = max; input.value = get();
  const val = document.createElement('b'); val.textContent = get();
  input.addEventListener('input', () => { set(+input.value); val.textContent = input.value; refresh(); });
  l.append(name, input, val);
  into.appendChild(l);
  return () => { input.value = get(); val.textContent = get(); };
}

/* THE KIT, READ OUT OF `ITEMS` (2026-09-08; Vijay: "we also need to revitalize the playground so we
 * have a place to test everything thoroughly", alongside "at least 100 collectible items before the
 * game is considered shippable").
 *
 * The playground could spawn every creature -- that list is generated from `ENEMY_DEFS` and so has
 * never gone stale -- and it could not hand you a single ITEM. No weapon but the starting sickle, no
 * gear, nothing to wear or wield or carry. For a bench meant to test everything, the half of the
 * game you hold was missing.
 *
 * This is generated the same way the spawn row is, straight out of `ITEMS`, so the hundredth item
 * will appear here the day it is defined and nobody has to remember this file. It groups by what the
 * table already says a thing IS -- a weapon, a piece of gear, or something you carry -- rather than
 * by a list kept here, for the same reason. */
const KIND_OF = (k: string) => (ITEMS[k].weapon ? 'wield' : ITEMS[k].gear ? 'wear' : 'carry');
for (const group of ['wield', 'wear', 'carry']) {
  const kinds = Object.keys(ITEMS).filter((k) => KIND_OF(k) === group && k !== 'fist');
  if (!kinds.length) continue;
  const tag = document.createElement('b');
  tag.className = 'grouplabel';
  tag.textContent = group;
  $('kit').appendChild(tag);
  for (const kind of kinds) {
    button($('kit'), ITEMS[kind].name || kind, () => {
      /* THE BENCH MAKES ROOM (2026-09-15). The kit row is generated from ITEMS, and the game now has more KINDS
       * (48, on the way to a hundred) than the bag has SLOTS (27) -- so once the row had been clicked through, the
       * pieces at the end of it could not be added at all, and the check that the bench "can hand you everything"
       * found nothing worn. A bench is not a run: if the bag is full it gives up its last slot. */
      const b3 = bagOf(game.state);
      if (b3.slots.every((s3: any) => s3)) b3.slots[b3.slots.length - 1] = null;
      addItem(game.state, kind);
      let at = bagOf(game.state).slots.findIndex((s2: any) => s2 && s2.kind === kind);
      /* ...AND IT HAS TO BE IN THE HOTBAR TO BE PICKED (2026-09-15). `selectSlot` wraps its index into the nine
       * hotbar slots -- ((i % 9) + 9) % 9 -- so selecting slot 13 selects slot 4, and `wear` then tried to put on
       * whatever weapon was sitting there and quietly returned undefined. With four weapons in the game the gear
       * landed inside the hotbar and this worked by luck; with twelve it lands past it and EVERY gear button stopped
       * wearing anything (traced button by button: eighteen pieces in the bag, nothing on the body). The piece is
       * swapped into slot 0 first, which is what a bench should do anyway. */
      if (at >= HOTBAR_SLOTS) {
        const b4 = bagOf(game.state); const tmp = b4.slots[0]; b4.slots[0] = b4.slots[at]; b4.slots[at] = tmp; at = 0;
      }
      if (at >= 0) {
        selectSlot(game.state, at);
        if (ITEMS[kind].gear) { try { wear(game.state); } catch (err) { /* `wear` puts on what is picked; a piece that will not go on is not an error here */ } }
      }
      say((group === 'wear' ? 'worn: ' : group === 'wield' ? 'in hand: ' : 'in the bag: ') + (ITEMS[kind].name || kind));
    });
  }
}
button($('kit'), 'empty the bag', () => {
  const b2 = bagOf(game.state);
  for (let i = 0; i < b2.slots.length; i++) b2.slots[i] = null;
  say('the bag is empty');
}, 'warn');

// --- charms
const CHARM = [['pods', 'the rattle'], ['tide', 'the silt lung'], ['ember', 'the emberwake']];
const FLAG = { pods: 'hasPods', tide: 'hasTide', ember: 'hasEmber' };
const charmBtns: any[] = [];
for (const [tool, name] of CHARM) {
  charmBtns.push([tool, button($('charms'), name, () => {
    const on = !game.state[FLAG[tool]];
    game.state[FLAG[tool]] = on;
    if (on) { game.state.tool = tool; if (tool === 'pods') game.state.pods = game.state.maxPods; }
    else if (game.state.tool === tool) game.state.tool = 'pods';
    say(name + (on ? ' taken' : ' put back'));
  })]);
}
button($('charms'), 'all three', () => {
  for (const [tool] of CHARM) game.state[FLAG[tool]] = true;
  game.state.pods = game.state.maxPods;
  say('all three charms');
});

// --- you
const syncers: any[] = [];
syncers.push(slider($('you'), 'vigor', 2, 48, () => game.state.maxHp,
  (v) => { game.state.maxHp = v; game.state.hp = v; }));
syncers.push(slider($('you'), 'pods', 0, 12, () => game.state.maxPods,
  (v) => { game.state.maxPods = v; game.state.pods = v; }));
syncers.push(slider($('you'), 'glim', 0, 300, () => game.state.glim,
  (v) => { game.state.glim = v; }));
/* TWO KNOBS ON FIELDS THE GAME DELETED (2026-09-18). `reach` and `ward` went with the shop on 2026-09-06 -- nothing
 * grants them, nothing reads them, and `save.ts` scrubs them off a loaded state by name. Dragging them made two
 * fields that did nothing and were thrown away on the next write, on a page whose whole purpose is to show what a
 * knob does. A reach knob wants a different WEAPON in the bench's hands, which the gear card already gives. */
const godBtn = button($('you'), 'cannot be hurt', () => {
  god = !god;
  say(god ? 'nothing can hurt you' : 'you can be hurt again');
});
button($('you'), 'heal', () => { game.state.hp = game.state.maxHp; say('mended'); });
let god = true;

// --- spawn
for (const kind of Object.keys(ENEMY_DEFS)) {
  button($('spawn'), kind, () => {
    const rng = new RNG((Math.round(game.frame) * 2654435761) >>> 0);
    const before = game.enemies.length;
    game.placeEnemy(kind, rng, { hpBonus: 0 });
    say(game.enemies.length > before ? 'a ' + kind + ' arrives' : 'no room for a ' + kind);
  });
}
button($('spawn'), 'clear', () => { game.enemies = []; say('room cleared'); }, 'warn');

// --- paint
// ...IN THE SAME VOICE AS EVERYTHING ELSE. These were the only capitals left
// on a page whose every other control is lower case, and the game they
// demonstrate speaks in lower case throughout.
/** [label, tileKind]. Annotated because an array of mixed-type rows widens
 *  to `(string | number)[]` and the kind stops being a number. */
/* THE BRUSHES ARE LIVE TILES ONLY (2026-09-08). `floor`, `wall` and `plate` painted RETIRED ids --
 * the hollows' tiles, kept only so the live ones keep their numbers -- so three of the eleven brushes
 * laid down something nothing could walk on and nothing would draw properly. */
const PAINT: Array<[string, number]> = [['ground', T.GROUND], ['moss', T.MOSS], ['sand', T.SAND],
  ['water', T.WATER], ['shallow', T.SHALLOW], ['thorn', T.THORN], ['rubble', T.RUBBLE],
  ['crack', T.CRACK], ['cliff', T.CLIFF], ['rock', T.ROCK], ['ramp', T.RAMP],
  ['brazier', T.BRAZIER], ['push', T.PUSH], ['nest', T.NEST]];
let brush = T.THORN;
/** [tileKind, buttonElement] — a tuple, so the kind stays a number. */
const paintBtns: Array<[number, HTMLElement]> = [];
for (const [label, kind] of PAINT) {
  paintBtns.push([kind, button($('paint'), label, () => { brush = kind; })]);
}
/* THE BRUSH LAYS THE TILE DOWN WHERE YOU CLICKED (2026-09-18). This turned canvas pixels straight into tile indices,
 * as though the picture were the screen's own grid at the world's origin. The arena is a wild screen and the window is
 * `world - camera`, which never aligns to a screen: the card says "click the picture to lay it down" and it laid it
 * down up to three tiles away, on whatever the camera's scroll happened to make of the point. The camera goes in
 * first, and a click that falls on a NEIGHBOUR screen is dropped, because only the loaded screen has `tiles`. */
screenCanvas.addEventListener('pointerdown', (e) => {
  const r = screenCanvas.getBoundingClientRect();
  const sx = (e.clientX - r.left) / r.width * VW;
  const sy = (e.clientY - r.top) / r.height * VH - HUD_H;
  if (sy < 0) return;   // the click was on the HUD, not the world
  const cam = game.camera();
  const wx = cam.x + sx, wy = cam.y + sy;
  const scrX = Math.floor(wx / (COLS * TS)), scrY = Math.floor(wy / (ROWS * TS));
  if (game.area.kind === 'wild' && (scrX !== game.area.x || scrY !== game.area.y)) return;
  const tx = Math.floor(wx / TS) - (game.area.kind === 'wild' ? scrX * COLS : 0);
  const ty = Math.floor(wy / TS) - (game.area.kind === 'wild' ? scrY * ROWS : 0);
  if (tx < 0 || ty < 0 || tx >= COLS || ty >= ROWS) return;
  game.tiles[ty][tx] = brush;
  if (game.markBroken) game.markBroken(tx, ty);   // a painted tile is kept the way a broken one is, or the next bake paints over it
  e.preventDefault();
});

// --- room
button($('room'), 'rebuild', () => buildRoom());
button($('room'), 'night', () => {
  game.night = !game.night;
  say(game.night ? 'the long night' : 'daylight');
});
/* THE `dark room` BUTTON IS GONE WITH THE ROOMS (2026-09-06). It read `game.room` -- a getter that looked up the
 * room in the hollow the player was standing in -- and toggled that room's `dark` trait. There are no hollows and no
 * rooms since the pivot, so the getter is undefined and every press answered `step into a hollow first`, which is
 * advice nobody can take. Its own comment records it being inert for three rounds of review for a different reason
 * before that. */
button($('room'), 'filter', () => {
  const order = ['crisp', 'tube', 'flat'];
  const next = order[(order.indexOf(display.preset || 'crisp') + 1) % order.length];
  display.set(next);
  say('filter: ' + next);
});

// --- stunts: the things that are hard to see happen in a real run
button($('stunts'), 'light a fire', () => {
  game.state.hasEmber = true;
  game.state.tool = 'ember';
  game.state.emberCool = 0;
  game.useTool();
  say('an ember — watch it take the hedge');
});
button($('stunts'), 'arm a pod', () => {
  game.state.hasPods = true;
  game.state.tool = 'pods';
  if (!game.state.pods) game.state.pods = game.state.maxPods;
  game.useTool();
  say('a pod, with a short fuse');
});
button($('stunts'), 'throw me', () => {
  game.player.launch(1, 0);
  say('the blast pitches you three tiles');
});
button($('stunts'), 'charge the throw', () => {
  game.state.beam = 1;
  say('the sickle is ready to throw — press Z');
});
button($('stunts'), 'fill the room', () => {
  const kinds = Object.keys(ENEMY_DEFS).filter((k) => !ENEMY_DEFS[k].boss);
  for (let i = 0; i < kinds.length; i++) {
    game.placeEnemy(kinds[i], new RNG(1000 + i), { hpBonus: 0 });
  }
  say('one of each, all at once');
});

// --------------------------------------------------------------- refresh
function refresh() {
  for (const [tool, b] of charmBtns) b.classList.toggle('on', !!game.state[FLAG[tool]]);
  for (const [kind, b] of paintBtns) b.classList.toggle('on', kind === brush);
  godBtn.classList.toggle('on', god);
  for (const s of syncers) s();
}

// ----------------------------------------------------------------- input
const held = new Set();
const KEYMAP = { ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down',
  ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right' };
addEventListener('keydown', (e) => {
  if (e.target && e.target.tagName === 'INPUT') return;
  if (KEYMAP[e.code]) { held.add(KEYMAP[e.code]); e.preventDefault(); }
  else if (e.code === 'KeyZ' || e.code === 'Space') { game.input.attackPressed = true; e.preventDefault(); }
  else if (e.code === 'KeyX' || e.code === 'KeyC') { game.input.podPressed = true; game.input.podHeld = true; e.preventDefault(); }
  else if (e.code === 'KeyQ' && !e.repeat) { game.cycleTool(); refresh(); e.preventDefault(); }
});
addEventListener('keyup', (e) => {
  if (KEYMAP[e.code]) held.delete(KEYMAP[e.code]);
  if (e.code === 'KeyX' || e.code === 'KeyC') game.input.podHeld = false;
});
// --- TOUCH, DRIVEN BY THE GAME'S OWN PAD. Not a copy of the stick: the same
// `Pad` from src/pad.js and the same stylesheet the game links, so a diagonal
// here behaves exactly as a diagonal there. A sandbox whose controls are a
// near-copy of the game's teaches you the near-copy.
const isTouch = matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
if (isTouch) document.body.classList.add('touch');
const padEl = document.getElementById('pad') as any;
const knobEl = padEl && padEl.querySelector('.knob');
const padState = new Pad();
let knobAt = '';
const padXY = (ev) => {
  const r = padEl.getBoundingClientRect();
  return [ev.clientX - (r.left + r.width / 2), ev.clientY - (r.top + r.height / 2), r.width];
};
function paintPad() {
  const k = padState.knob();
  const to = k ? 'translate(' + k.x.toFixed(1) + 'px,' + k.y.toFixed(1) + 'px)' : '';
  if (to !== knobAt) { knobEl.style.transform = to; knobAt = to; }
  knobEl.classList.toggle('held', !!k);
}
if (padEl) {
  padEl.addEventListener('pointerdown', (e) => {
    const [x, y, w] = padXY(e);
    if (padState.down(e.pointerId, x, y, w)) {
      try { padEl.setPointerCapture(e.pointerId); } catch (err) { /* synthetic */ }
      paintPad();
    }
    e.preventDefault();
  });
  padEl.addEventListener('pointermove', (e) => {
    const [x, y, w] = padXY(e);
    if (padState.move(e.pointerId, x, y, w)) { paintPad(); e.preventDefault(); }
  });
  for (const evt of ['pointerup', 'pointercancel', 'pointerleave']) {
    padEl.addEventListener(evt, (e) => { padState.up(e.pointerId); paintPad(); });
  }
}
/** The action buttons, with the same press-and-latch feel as the game's. */
function touchButton(id, fn) {
  const el = document.getElementById(id) as any;
  if (!el) return;
  let timer: any = 0;
  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    fn(true);
    el.classList.add('on');
    clearTimeout(timer);
    timer = setTimeout(() => el.classList.remove('on'), 90);
    navigator.vibrate?.(8);
  });
  for (const evt of ['pointerup', 'pointercancel', 'pointerleave']) {
    el.addEventListener(evt, () => fn(false));
  }
}
touchButton('btn-a', (down) => { if (down) game.input.attackPressed = true; });
touchButton('btn-b', (down) => {
  if (down) game.input.podPressed = true;
  game.input.podHeld = down;
});
(document.getElementById('btn-tool') as any)?.addEventListener('pointerdown', (e) => {
  e.preventDefault(); game.cycleTool(); refresh();
});
/* THE BUTTONS SAY WHAT THE GAME'S BUTTONS SAY, FROM THE GAME'S OWN RULE.
 *
 * This carried its own `TOOL_SAYS` table — three verbs, hand-copied — and it
 * had already drifted three ways: it disagreed on case (`DIVE` against the
 * game's `dive`), it knew nothing about the second verbs (a pod already down,
 * a full lung on dry land, a brazier ahead), and it wrote `textContent` only,
 * so the spoken name stayed whatever `index.html` had said at load: the
 * button reading `THROW` was announced as "Swing the sickle".
 *
 * This file's own header says a sandbox that reimplements the game in order
 * to demonstrate it is a second game that will disagree with the first. It
 * did. It reads `src/labels.ts` now, which is the one place that rule lives.
 */
function syncTouchLabels() {
  const b = document.getElementById('btn-b') as any;
  if (!b) return;
  const say = actionLabel(game.equipped(), game.state, game.toolContext());
  if (b.textContent !== say.text) {
    b.textContent = say.text;
    b.setAttribute('aria-label', say.aria);
  }
  const a = document.getElementById('btn-a') as any;
  if (!a) return;
  const swing = swingLabel(game.state.beam >= 1);
  if (a.textContent !== swing.text) {
    a.textContent = swing.text;
    a.setAttribute('aria-label', swing.aria);
  }
}

// ------------------------------------------------------------------ loop
function resize() {
  const touch = document.body.classList.contains('touch');
  const w = Math.min(innerWidth - (touch ? 16 : 28), 760);
  // leave room for the stick and the buttons underneath on a phone
  const h = innerHeight - (touch ? 250 : 40);
  display.ok ? display.resize(w, h, devicePixelRatio) : (() => {
    screenCanvas.width = VW; screenCanvas.height = VH;
    screenCanvas.style.width = Math.floor(Math.min(w / VW, h / VH)) * VW + 'px';
  })();
}
addEventListener('resize', resize);
resize();

const STEP = 1000 / 60;
let last = performance.now(), acc = 0;
function frame(now) {
  acc += Math.min(now - last, 250);
  last = now;
  if (padEl) {
    padState.steering = !game.canSteer || game.canSteer();
    // Keys are cardinals; the stick is a vector. Same two sentences as the
    // game — a sandbox whose movement is a near-copy of the game's teaches
    // you the near-copy.
    for (const d of KEYS) game.input[d] = padState.steering && held.has(d);
    const v = padState.vector();
    game.input.ax = v ? v.x * v.mag : 0;
    game.input.ay = v ? v.y * v.mag : 0;
    paintPad();
  }
  syncTouchLabels();
  try {
    while (acc >= STEP) {
      game.update();
      // NOTHING AT STAKE. The panel says you cannot be hurt, so it has to be
      // true — and it is enforced after the update rather than by disabling
      // damage, so every hit still LANDS and you can watch what it does.
      if (god) { game.state.hp = game.state.maxHp; game.mode = 'play'; }
      // ...and the guard the save path sets stays off, since there is no save
      game.otherTab = false;
      game.saveQuarantined = false;
      game.storageFailed = false;
      acc -= STEP;
    }
    /* ...AND IT HOLDS THE ROOM, because one build is not enough. The paced pre-bake reassigns
     * `game.tiles` to freshly baked parts over SEVERAL frames, so a room built at load or on frame
     * one is thrown away again a few frames later -- which is why pressing `rebuild` by hand always
     * worked and the bench always opened on open sea. The arena is cheap to check: if the middle of
     * the floor is not the floor any more, the room has been baked over, so lay it down again. */
    if (!isArena(game.tiles)) buildRoom(quiet);
    game.render();
    display.draw();
  } catch (err) {
    acc = 0;
    say('the game threw: ' + (err && err.message));
    console.error(err);
  }
  requestAnimationFrame(frame);
}

/* THE ROOM IS BUILT ON THE FIRST FRAME, NOT AT LOAD (2026-09-08).
 *
 * `buildRoom()` was called here, straight after `reset` and `startGame`, and it did nothing you could
 * see: the bench opened on WORLD 0,0 -- open sea, the island's north-west corner -- and every spawn
 * answered "no room". The paint was real and then it was thrown away, because the paced pre-bake
 * rebuilds a screen's parts and reassigns `game.tiles` to the new array; anything written into the
 * old one goes with it. Pressing `rebuild` after the loop had settled always worked, which is what
 * made this look like an arena bug rather than an ordering one.
 *
 * So it is built from inside the first frame, once the loop owns the screen. */
for (const [tool] of CHARM) game.state[FLAG[tool]] = true;
game.state.pods = game.state.maxPods;
game.state.glim = 120;
refresh();
requestAnimationFrame(frame);
window.game = game;
window.display = display;
